<template>
  <div class="register-container">
    <div class="register-form">
      <h2>{{ $t('register.title') }}</h2>
      <form @submit.prevent="handleRegister">
        <div class="form-group">
          <label for="username">{{ $t('register.username') }}:</label>
          <input 
            id="username" 
            v-model="registerForm.username" 
            type="text" 
            required 
            :placeholder="$t('register.placeholder_username')"
          />
        </div>
        <div class="form-group">
          <label for="email">{{ $t('register.email') }}:</label>
          <input 
            id="email" 
            v-model="registerForm.email" 
            type="email" 
            required 
            :placeholder="$t('register.placeholder_email')"
          />
        </div>
        <div class="form-group">
          <label for="password">{{ $t('register.password') }}:</label>
          <input 
            id="password" 
            v-model="registerForm.password" 
            type="password" 
            required 
            :placeholder="$t('register.placeholder_password')"
            minlength="6"
          />
        </div>
        <div class="form-group">
          <label for="confirmPassword">{{ $t('register.confirmPassword') }}:</label>
          <input 
            id="confirmPassword" 
            v-model="registerForm.confirmPassword" 
            type="password" 
            required 
            :placeholder="$t('register.placeholder_confirm_password')"
          />
        </div>
        <div class="form-group">
          <label for="company">{{ $t('register.company') }}:</label>
          <input 
            id="company" 
            v-model="registerForm.company" 
            type="text" 
            :placeholder="$t('register.placeholder_company')"
          />
        </div>
        <button type="submit" :disabled="loading">
          {{ loading ? $t('register.registering') : $t('register.register') }}
        </button>
      </form>
      <p class="login-link">
        {{ $t('register.already_have_account') }} <router-link to="/login">{{ $t('register.login_now') }}</router-link>
      </p>
      <div v-if="errorMessage" class="error-message">
        {{ errorMessage }}
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { encryptUserData, decryptUserData } from '../utils/web-crypto'

export default {
  name: 'Register',
  data() {
    return {
      registerForm: {
        username: '',
        email: '',
        password: '',
        confirmPassword: '',
        company: ''
      },
      loading: false,
      errorMessage: ''
    }
  },
  methods: {
    async handleRegister() {
      this.loading = true
      this.errorMessage = ''
      
      // 验证邮箱格式
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
      if (!emailRegex.test(this.registerForm.email)) {
        this.errorMessage = this.$t('register.error_invalid_email')
        this.loading = false
        return
      }
      
      // 验证密码一致性
      if (this.registerForm.password !== this.registerForm.confirmPassword) {
        this.errorMessage = this.$t('register.error_password_mismatch')
        this.loading = false
        return
      }
      
      try {
        // 准备用户数据
        const userData = {
          u: this.registerForm.username,
          e: this.registerForm.email,
          p: this.registerForm.password,
          c: this.registerForm.company || ''  
        };
        
        // 加密用户数据
        const encryptedData = await encryptUserData(userData);
        
        // 发送加密的请求
        const response = await axios.post('/api/auth/register', { encryptedData }, {
          headers: {
            'Accept-Language': this.$i18n.locale,
            'x-client-id': 'web'
          }
        })
        
        if (response.data.success) {
          // 解密服务器返回的加密注册数据
          const decryptedRegistrationData = await decryptUserData(response.data.encryptedData);
          
          // 保存必要信息到本地存储
          localStorage.setItem('token', decryptedRegistrationData.t || '');
          localStorage.setItem('userInfo', JSON.stringify(decryptedRegistrationData));
          
          // 注册成功，跳转到登录页面并显示成功消息
          this.$router.push('/login?registrationSuccess=true')
        } else {
          // 直接显示后端返回的国际化错误消息
          this.errorMessage = response.data.message
        }
      } catch (error) {
        console.error('注册错误详情:', error);
        if (error.response && error.response.data && error.response.data.message) {
          // 处理服务器返回的具体错误
          this.errorMessage = error.response.data.message
        } else {
          this.errorMessage = this.$t('register.error_generic')
        }
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.register-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
  padding: 20px;
}

.register-form {
  width: 100%;
  max-width: 400px;
  background: #fff;
  padding: 30px;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.register-form h2 {
  text-align: center;
  margin-bottom: 20px;
  color: #333;
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
  color: #555;
}

.form-group input {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
  box-sizing: border-box;
}

button {
  width: 100%;
  padding: 12px;
  background-color: #28a745;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.3s;
}

button:hover:not(:disabled) {
  background-color: #218838;
}

button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.login-link {
  text-align: center;
  margin-top: 15px;
  color: #666;
}

.login-link a {
  color: #28a745;
  text-decoration: none;
}

.login-link a:hover {
  text-decoration: underline;
}

.error-message {
  margin-top: 15px;
  padding: 10px;
  background-color: #f8d7da;
  color: #721c24;
  border-radius: 4px;
  text-align: center;
}
</style>
